import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import isEmpty from 'lodash/isEmpty';
import {
  Form,
  ButtonsWrapper,
  Button,
  TextInput,
  SelectInput,
  UploadInput,
} from '@thinknet-gmbh/platform-common-ui';

import {
  FINISH_INFORMATION_QUESTIONNAIRES,
} from '@thinknet-gmbh/platform-common';

import { useGetAllEmailTemplate } from 'src/hooks/email-templates/get-all-platform-email-template.hook';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';

import { ActionFooter } from './components/action-footer';
import { QuestionnaireListItem } from './components/questionnaire-list-item';

import {
  AcceptedListContainer,
  EditEmployeeQuestionnaireSettingWrapper,
  FormBodyContainer,
  HeaderFormContainer,
  PreviewListContainer,
  ListContainer,
  ListWrapper,
  FieldContainer,
} from './employee-questionnaire-settings-form.styles';

import { IEmployeeQuestionnaireSettingsEditFormProps } from '../../employee-questionnaire-settings-interfaces';


const isItemInclude = (items: string[], item: string) => items.includes(item);

const getAllWithoutItem = (items: string[], item: string) =>
  items.filter(element => element !== item);

const EditEmployeeQuestionnaireSettingsForm = ({
  acceptedListData,
  previewListData,
  onFinishHandler,
  questionnaireSetting,
}: IEmployeeQuestionnaireSettingsEditFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [acceptedList, setAcceptedList] = useState<string[]>(acceptedListData);
  const [previewList, setPreviewList] = useState<string[]>(previewListData);
  const [selectedAcceptedListItems, setSelectedAcceptedListItems] = useState<string[]>([]);
  const [selectedPreviewListItems, setSelectedPreviewListItems] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const { templates = [], loading } = useGetAllEmailTemplate();

  const {
    name,
    privacyPolicyLink,
    supervisorEmailTemplateId,
    applicantEmailTemplateId,
    miniJobDocument,
  } = questionnaireSetting || {};

  const onDragStart = (index: number) => () => {
    setDraggedIndex(index);
  };

  const onDragOver = (index: number) => (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      const updatedItems = [...acceptedList];
      const [removedItem] = updatedItems.splice(draggedIndex, 1);
      updatedItems.splice(index, 0, removedItem);
      setDraggedIndex(index);
      setAcceptedList(updatedItems);
    }
  };

  const onDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleMoveToAccept = () => {
    setAcceptedList([...acceptedList, ...selectedPreviewListItems]);
    setPreviewList(previewList.filter(item => !selectedPreviewListItems.includes(item)));
    setSelectedPreviewListItems([]);
  };

  const handleMoveToPreview = () => {
    setPreviewList([...previewList, ...selectedAcceptedListItems]);
    setAcceptedList(acceptedList.filter(item => !selectedAcceptedListItems.includes(item)));
    setSelectedAcceptedListItems([]);
  };

  const handleMoveOneToAccept = (item: string) => () => {
    setAcceptedList([...acceptedList, item]);
    setPreviewList(previewList.filter(el => el !== item));
    setSelectedPreviewListItems(selectedPreviewListItems.filter(el => el !== item));
  };

  const handleMoveOneToPreview = (item: string) => () => {
    setPreviewList([...previewList, item]);
    setAcceptedList(acceptedList.filter(el => el !== item));
    setSelectedAcceptedListItems(selectedAcceptedListItems.filter(el => el !== item));
  };

  const handleAcceptedItemOnChange = (item: string) => () => {
    setSelectedAcceptedListItems(prev =>
      isItemInclude(prev, item) ? getAllWithoutItem(prev, item) : [...prev, item]
    );
  };

  const handleMoveToPreviewALL = () => {
    setPreviewList([...previewList, ...acceptedList]);
    setAcceptedList([]);
    setSelectedAcceptedListItems([]);
    setSelectedPreviewListItems([]);
  };

  const handleMoveToAcceptAll = () => {
    setAcceptedList([...acceptedList, ...previewList]);
    setPreviewList([]);
    setSelectedAcceptedListItems([]);
    setSelectedPreviewListItems([]);
  };

  const handlePreviewItemOnChange = (item: string) => () => {
    setSelectedPreviewListItems(prev =>
      isItemInclude(prev, item) ? getAllWithoutItem(prev, item) : [...prev, item]
    );
  };

  const isLinkRequired = acceptedList.includes(FINISH_INFORMATION_QUESTIONNAIRES.privacyPolicyConfirmation);
  const isMiniJobRequired = acceptedList.includes(FINISH_INFORMATION_QUESTIONNAIRES.miniJobDocument);

  return (
    <EditEmployeeQuestionnaireSettingWrapper>
      <Form
        layout="vertical"
        onFinish={values => onFinishHandler(values, acceptedList)}
        initialValues={{
          name: name || '',
          supervisorEmailTemplateId: supervisorEmailTemplateId || '',
          applicantEmailTemplateId: applicantEmailTemplateId || '',
          settings: acceptedListData,
          privacyPolicyLink: privacyPolicyLink || '',
          uploadedFile: miniJobDocument
        }}
      >
        <FormBodyContainer>
          <HeaderFormContainer>
            <FieldContainer>
              <Form.Item
                name="name"
                label={t('settings:employeeQuestionnaire.labels.enterName')}
                rules={[
                  { required: true, message: t('settings:employeeQuestionnaire.validation.name') },
                ]}
              >
                <TextInput placeholder={t('settings:employeeQuestionnaire.labels.enterName')} />
              </Form.Item>

              {!loading && (
                <Fragment>
                  <Form.Item
                    name="applicantEmailTemplateId"
                    label={t('settings:employeeQuestionnaire.labels.applicantEmailTemplate')}
                    rules={[
                      { required: true, message: t('common:validation.error.fieldRequired') },
                    ]}
                  >
                    <SelectInput
                      placeholder={t(
                        'settings:employeeQuestionnaire.labels.applicantEmailTemplate'
                      )}
                    >
                      {templates?.map(({ _id, subject }: { _id: string; subject: string }) => (
                        <SelectInput.Option key={_id} value={_id}>
                          {subject}
                        </SelectInput.Option>
                      ))}
                    </SelectInput>
                  </Form.Item>

                  <Form.Item
                    name="supervisorEmailTemplateId"
                    label={t('settings:employeeQuestionnaire.labels.supervisorEmailTemplate')}
                    rules={[
                      { required: true, message: t('common:validation.error.fieldRequired') },
                    ]}
                  >
                    <SelectInput
                      placeholder={t(
                        'settings:employeeQuestionnaire.labels.supervisorEmailTemplate'
                      )}
                    >
                      {templates?.map(({ _id, subject }: { _id: string; subject: string }) => (
                        <SelectInput.Option key={_id} value={_id}>
                          {subject}
                        </SelectInput.Option>
                      ))}
                    </SelectInput>
                  </Form.Item>
                </Fragment>
              )}

              <Form.Item
                name="privacyPolicyLink"
                label={t('settings:employeeQuestionnaire.labels.privacyPolicyLink')}
                rules={[
                  {
                    required: isLinkRequired,
                    message: t('settings:employeeQuestionnaire.validation.privacyPolicyLink'),
                  },
                ]}
              >
                <TextInput placeholder="http://...." />
              </Form.Item>

              <Form.Item
                name="uploadedFile"
                className={'upload-file-field'}
                valuePropName="file"
                rules={[
                  {
                    required: isMiniJobRequired,
                    message: t('common:validation.error.fieldRequired'),
                  },
                ]}
                label={t('settings:employeeQuestionnaire.questionnaireInfo.miniJobDocument')}
              >
                <UploadInput
                  showUploadList
                  beforeUpload={() => false}
                  variant="DashedContainer"
                  btnText={t('settings:documents.buttons.uploadFile')}
                  accept=".pdf"
                  maxCount={1}
                  defaultFileList={
                    miniJobDocument
                      ? [
                        {
                          uid: miniJobDocument.key,
                          name: miniJobDocument.filename,
                          url: miniJobDocument.location,
                        },
                      ]
                      : undefined
                  }
                />
              </Form.Item>
            </FieldContainer>

            <ButtonsWrapper marginTop={30}>
              <Button htmlType="submit" type="primary">
                {t('common:buttons.save')}
              </Button>
              <Button
                onClick={() =>
                  navigate(`${ROUTES.settings}?type=${SETTINGS_PAGES.employeeQuestionnaire}`)
                }
              >
                {t('common:buttons.cancel')}
              </Button>
            </ButtonsWrapper>
          </HeaderFormContainer>
          <ListWrapper>
            <PreviewListContainer>
              <ListContainer selectedItem={selectedPreviewListItems.length > 0}>
                {previewList.map((item, index) => (
                  <QuestionnaireListItem
                    index={index}
                    key={`${item}-PreviewList`}
                    checked={isItemInclude(selectedPreviewListItems, item)}
                    onChange={handlePreviewItemOnChange(item)}
                    item={item}
                    buttonPosition={'left'}
                    handleMoveTo={handleMoveOneToAccept(item)}
                  />
                ))}
              </ListContainer>
              {!isEmpty(selectedPreviewListItems) && (
                <ActionFooter
                  count={selectedPreviewListItems.length}
                  buttonPosition={'left'}
                  handleMove={handleMoveToAccept}
                  handleMoveAll={handleMoveToAcceptAll}
                  countAll={previewList.length}
                />
              )}
            </PreviewListContainer>
            <AcceptedListContainer>
              <ListContainer selectedItem={selectedAcceptedListItems.length > 0}>
                {acceptedList.map((item, index) => (
                  <QuestionnaireListItem
                    index={index}
                    draggable={true}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragOver={onDragOver}
                    key={`${item}-acceptedList`}
                    checked={isItemInclude(selectedAcceptedListItems, item)}
                    onChange={handleAcceptedItemOnChange(item)}
                    item={item}
                    buttonPosition={'right'}
                    handleMoveTo={handleMoveOneToPreview(item)}
                  />
                ))}
              </ListContainer>
              {!isEmpty(selectedAcceptedListItems) && (
                <ActionFooter
                  count={selectedAcceptedListItems.length}
                  buttonPosition={'right'}
                  handleMove={handleMoveToPreview}
                  handleMoveAll={handleMoveToPreviewALL}
                  countAll={acceptedList.length}
                />
              )}
            </AcceptedListContainer>
          </ListWrapper>
        </FormBodyContainer>
      </Form>
    </EditEmployeeQuestionnaireSettingWrapper>
  );
};

export default EditEmployeeQuestionnaireSettingsForm;
